import {Listbox, Transition} from "@headlessui/react";
import {FC, Fragment, PropsWithChildren, useEffect, useState} from "react";
import {FaAngleDown, FaCheck} from "react-icons/fa";
import {CgSpinner} from "react-icons/cg";
import { motion } from "framer-motion";

export type onSelectChangeHandler<T> = (type: OptionsType<T>) => void;

interface Props<TValueType> {
    options: OptionsType<TValueType>[],
    defaultValue?: OptionsType<TValueType>,
    onChange?:onSelectChangeHandler<TValueType>
    loading?: boolean;
    placeholder?: string;
    small?: boolean;
}

type Options<T> = {
    value: T
}
type OptionsType<T> = Options<T> & {
    label: string;
}

function Select<T>(props: PropsWithChildren<Props<T>>) {
    const [selected, setSelected] = useState<OptionsType<T>|undefined>(props.defaultValue);

    const onChange = (value: OptionsType<T>) => {
        if(props.onChange) props.onChange(value);
        setSelected(value);
    }

    useEffect(() => {
        setSelected(props.defaultValue);
    },[props.options, props.defaultValue])

    return (
        <motion.div initial={{translateX: -50, opacity:0}} animate={{translateX:0, opacity:1}} exit={{translateX: -50, opacity:0}}>
            <Listbox value={selected} disabled={props.loading} onChange={onChange}>
                <div className="relative mt-1">
                    <Listbox.Button
                        className={`relative w-full py-3 pl-4 pr-10 text-left bg-transparent shadow dark:border border-cardGray placeholder-cardGray dark:border-slate-600 rounded disabled:cursor-progress cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm ${(!selected) ? 'text-accentGray dark:text-accentGray' : 'text-black dark:text-white'} ${props.small && "py-1 pr-7 border-none"}`}>
                        <span className="block truncate">{!selected ? (props.placeholder ? props.placeholder : 'Select value') : selected?.label}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">{props.loading ? <CgSpinner className={`text-xl animate-spin ${props.small && 'text-sm'}`}/> : <FaAngleDown className={`text-xl ${props.small && 'text-sm'}`}/>}</span>
                    </Listbox.Button>
                    {props.options && (
                        <Transition as={Fragment} leave='transition ease-in duration-100' leaveFrom="opacity-100"
                                    leaveTo="opacity-0">
                            <Listbox.Options className='absolute z-10 w-full py-1 mt-1 overflow-auto text-base max-h-60 shadow-lg rounded-lg bg-gray-200 text-black dark:text-white dark:bg-cardGray'>
                                {props.options.length > 0 ? (
                                    props.options.map((option, idx) => (
                                            <Listbox.Option key={idx} value={option} className={({ active }) =>
                                                `sm:text-sm cursor-default select-none relative px-4 py-2 border-b dark:border-white/5 border-black/5 last:border-none ${
                                                    active ? 'text-white bg-primary' : 'dark:text-white text-black'
                                                }`
                                            }>
                                                {(values) => (
                                                    <>
                                                        <div className='flex justify-start items-center'>{option.label} {values.selected && <FaCheck className='inline text-sm ml-2'/>}</div>
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))
                                ) : (
                                    <Listbox.Option value={null} disabled={true} className={({ active }) =>
                                        `sm:text-sm cursor-default select-none relative px-4 py-2 border-b border-white/5 last:border-none ${
                                            active ? 'text-white bg-primary' : 'text-white'
                                        }`
                                    }>
                                        {(values) => (
                                            <>
                                                <div className='flex justify-start items-center'>Keine Daten Verfügbar</div>
                                            </>
                                        )}
                                    </Listbox.Option>
                                )}
                            </Listbox.Options>
                        </Transition>
                    )}
                </div>
            </Listbox>
        </motion.div>
    );
}

(Select as FC<Props<any>>).defaultProps = {
    loading: false
}

export default Select;